import classNames from "classnames";
import { useCallback, useMemo, useRef, useState } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";

import { SalesDashboardData } from "../../../data-store";
import useUserInfo from "../../../data-store/useUserInfo";
import {
  ImpactCategory,
  impactLongCategoryLabelTitle,
} from "../../../domain/impactCategories";
import { ImpactRating } from "../../../domain/impactRatings";
import AreaChartWithLineOverlay from "../../graphs/AreaChart/AreaChart";
import { highD, lowB, mediumC, veryHighE, veryLowA } from "../../graphs/colors";
import "./RatingDistributionChart.css";
import Label from "../../labels/Label";
import { LinkButton } from "../../utils/Button";
import PercentageLozenge from "../../utils/PercentageLozenge";
import TooltipOverlay from "../../utils/TooltipOverlay";
import NumbersIcon from "../../utils/Vectors/NumbersIcon";
import PercentageIcon from "../../utils/Vectors/PercentageIcon";
import { useXAxisLabels } from "../../utils/xAxixTimeLabels";
import "./RatingDistributionChart.css";
import "../SalesDashboardPage.css";
import { useImpactCategory } from "../ImpactCategoryProvider";

interface RatingDistributionChartProps {
  data: SalesDashboardData;
  period: "MONTHLY" | "QUARTERLY" | "YEARLY";
  displayAsPercentage?: boolean;
}

const ratingsMap: {
  [key: string]: ImpactRating;
} = {
  veryLow: "VLOW",
  low: "LOW",
  medium: "MEDIUM",
  high: "HIGH",
  veryHigh: "VHIGH",
};

const ratingColors: {
  [key: string]: string;
} = {
  veryLow: veryLowA,
  low: lowB,
  medium: mediumC,
  high: highD,
  veryHigh: veryHighE,
};

export function RatingDistributionChart(props: RatingDistributionChartProps) {
  const { impactCategory } = useImpactCategory();

  const intl = useIntl();
  const [{ locale }] = useUserInfo();

  const [displayAsPercentage, setDisplayAsPercentage] = useState(true);

  const ratingDistributionKey = useMemo(() => {
    if (impactCategory === "GHG") {
      return "ghgRatingDistribution";
    }
    if (impactCategory === "LAND_USE") {
      return "landUseRatingDistribution";
    }
    if (impactCategory === "WATER_USE") {
      return "waterUseRatingDistribution";
    }
    return "ghgRatingDistribution";
  }, [impactCategory]);

  const selectedPeriod = useMemo(() => {
    if (props.period === "MONTHLY") {
      return "monthlyData";
    } else if (props.period === "QUARTERLY") {
      return "quarterlyData";
    }
    return "monthlyData";
  }, [props.period]);

  const stackedChartData = useMemo(() => {
    const ratingKeys = ["veryHigh", "high", "medium", "low", "veryLow"];

    const chartData = [];
    const periodData = props.data?.[selectedPeriod] ?? [];
    const baseData = Array(periodData.length).fill(0);

    chartData.push({
      id: 0,
      label: "Base",
      temp: true,
      data: baseData,
      backgroundColor: "transparent",
      borderColor: "transparent",
      pointRadius: 0,
      fill: false,
      clip: false,
      rating: ratingsMap[ratingKeys[0]],
    });

    let currentIndex = 1;

    for (let i = 0; i < ratingKeys.length; i++) {
      const key = ratingKeys[i];
      const color = ratingColors[key];

      const layerData = periodData.map((entry: any) => {
        const value = entry[ratingDistributionKey][key];

        if (displayAsPercentage) {
          const total = ratingKeys.reduce(
            (sum, rk) => sum + entry[ratingDistributionKey][rk],
            0
          );
          return total > 0 ? (value / total) * 100 : 0;
        }

        return value;
      });
      if (layerData.every((val) => val === 0 || val == null)) {
        continue;
      }

      chartData.push({
        id: currentIndex,
        label: key,
        data: layerData,
        backgroundColor: color,
        borderColor: "transparent",
        pointBackgroundColor: color,
        pointBorderColor: "white",
        pointBorderWidth: 5,
        pointHoverBorderColor: "white",
        pointHoverRadius: 5,
        pointRadius: 0,
        fill: currentIndex - 1,
        clip: false,
        rating: ratingsMap[ratingKeys[i]],
        hoverBackgroundColor: color,
        hoverBorderColor: color,
      });

      currentIndex++;

      if (i < ratingKeys.length - 1) {
        chartData.push({
          id: currentIndex,
          label: `${key}-spacer`,
          temp: true,
          data: baseData,
          backgroundColor: color,
          borderColor: color,
          pointRadius: 0,
          fill: currentIndex - 1,
          clip: false,
          rating: ratingsMap[ratingKeys[i]],
        });

        currentIndex++;
      }
    }

    return chartData;
  }, [props.data, ratingDistributionKey, selectedPeriod, displayAsPercentage]);

  const [selected, setSelected] = useState(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [xAxisMin] = useState(0);
  const chartRef = useRef(null);
  const [selectedLabelPos, setSelectedLabelPos] = useState<{
    x: number | null;
    y: number | null;
  }>({
    x: null,
    y: null,
  });

  const xAxisTimeLabels = useXAxisLabels(props.period);

  const handleSelectedLabelIndex = useCallback(
    (selectedLabel, selectedPos) => {
      setSelected(
        selectedLabel == null ? selectedLabel : selectedLabel + xAxisMin
      );
      if (selectedLabel == null) return;
      const rect = (chartRef.current as any)?.getBoundingClientRect();
      const { innerWidth } = window;
      const isOutsideDataRange = stackedChartData.every(
        (d) => d.data.length < selectedLabel + 1
      );
      if (innerWidth > rect.x + selectedPos.x + 400) {
        setSelectedLabelPos({
          x: isOutsideDataRange
            ? rect.x + selectedPos.x - 50
            : rect.x + selectedPos.x + 40,
          y: isOutsideDataRange ? rect.y + 80 : rect.y - 0,
        });
      } else {
        setSelectedLabelPos({
          x: isOutsideDataRange
            ? rect.x + selectedPos.x - 130
            : rect.x + selectedPos.x - 130,
          y: isOutsideDataRange ? rect.y - 180 : rect.y - 180,
        });
      }
    },
    [stackedChartData, xAxisMin]
  );

  return (
    <>
      <div
        style={{ overflow: "hidden" }}
        className="RatingDistributionChart_container"
      >
        {selected != null && selectedLabelPos.x && selectedLabelPos.y ? (
          <div
            style={{
              position: "absolute",
              left: selectedLabelPos.x,
              top: selectedLabelPos.y,
            }}
          >
            <Tooltip
              dataPoints={stackedChartData
                .filter((el) => !el.temp)
                .map((el, id) => {
                  return {
                    value: el?.data?.[selected],
                    color: el.backgroundColor,
                    rating: el.rating,
                    comparedToPreviousTimePeriod:
                      selected > 0 && el?.data?.[selected - 1]
                        ? (el?.data?.[selected] - el?.data?.[selected - 1]) /
                          el?.data?.[selected - 1]
                        : 0,
                  };
                })
                .slice(0)
                .reverse()}
              impactCategory={ImpactCategory.GHG}
              intl={intl}
              locale={locale}
              isSelectedLabel={selected == null ? false : true}
              isPercentage={displayAsPercentage}
              period={props.period}
              timePeriodLabel={xAxisTimeLabels[selected]}
              previousTimePeriodLabel={
                selected > 0
                  ? xAxisTimeLabels[selected - 1]
                  : xAxisTimeLabels[selected]
              }
            />
          </div>
        ) : null}
        <div ref={chartRef}>
          <AreaChartWithLineOverlay
            isPercentage={displayAsPercentage}
            xAxisMin={xAxisMin}
            xAxisMax={12}
            labels={xAxisTimeLabels}
            data={stackedChartData}
            dependentAxisLabel={
              displayAsPercentage
                ? intl.formatMessage({
                    id: "components/pages:SalesDashBoard/AreaChart/yAxisLabel/percentageOfSales",
                    defaultMessage: "Percentage of Sales (%)",
                  })
                : intl.formatMessage({
                    id: "components/pages:SalesDashBoard/AreaChart/yAxisLabel/salesNumber",
                    defaultMessage: "Sales (products sold)",
                  })
            }
            height={240}
            maintainAspectRatio={false}
            handleSelectedLabelIndex={handleSelectedLabelIndex}
            customTooltip={(tooltip) => {
              const dataPoints = stackedChartData
                .filter(
                  (el) => el.id === tooltip?.dataPoints?.[0]?.datasetIndex
                )
                .map((l) => ({
                  value: l?.data?.[tooltip?.dataPoints?.[0]?.dataIndex],
                  color: l.backgroundColor,
                  rating: l.rating,
                  comparedToPreviousTimePeriod:
                    tooltip?.dataPoints?.[0]?.dataIndex > 0 &&
                    l?.data?.[tooltip?.dataPoints?.[0]?.dataIndex - 1]
                      ? (l?.data?.[tooltip?.dataPoints?.[0]?.dataIndex] -
                          l?.data?.[tooltip?.dataPoints?.[0]?.dataIndex - 1]) /
                        l?.data?.[tooltip?.dataPoints?.[0]?.dataIndex - 1]
                      : 0,
                }));
              return (
                <Tooltip
                  dataPoints={dataPoints}
                  timePeriodTitle={`${tooltip?.dataPoints?.[0].label} ${props.data?.[selectedPeriod]?.[0].salesDataTimePeriod.year}`}
                  impactCategory={ImpactCategory.GHG}
                  intl={intl}
                  locale={locale}
                  isSelectedLabel={selected == null ? false : true}
                  isPercentage={displayAsPercentage}
                  period={props.period}
                  previousTimePeriodLabel={
                    tooltip?.dataPoints?.[0]?.datasetIndex > 0
                      ? xAxisTimeLabels[tooltip?.dataPoints?.[0]?.dataIndex - 1]
                      : xAxisTimeLabels[tooltip?.dataPoints?.[0]?.dataIndex]
                  }
                />
              );
            }}
          />
        </div>
        <div className="Rating_percentage_options d-flex">
          <TooltipOverlay
            id="sales-by-rating-percentage-tooltip"
            placement="top"
            overlay={
              <FormattedMessage
                id="components/pages:SalesDashBoard/AreaChart/percentageTooltip"
                defaultMessage="Display as percentage"
              />
            }
          >
            <LinkButton
              className={classNames("Rating_percentage_option", {
                Rating_percentage_option_selected: displayAsPercentage,
              })}
              onClick={() => setDisplayAsPercentage(true)}
            >
              <PercentageIcon width={20} />
            </LinkButton>
          </TooltipOverlay>
          <TooltipOverlay
            id="sales-by-rating-percentage-tooltip"
            placement="top"
            overlay={
              <FormattedMessage
                id="components/pages:SalesDashBoard/AreaChart/numberTooltip"
                defaultMessage="Display as number"
              />
            }
          >
            <LinkButton
              className={classNames(
                "Rating_percentage_option Rating_percentage_option_percentage",
                {
                  ChartsContainer_period_option_selected: !displayAsPercentage,
                }
              )}
              onClick={() => setDisplayAsPercentage(false)}
            >
              <NumbersIcon width={20} />
            </LinkButton>
          </TooltipOverlay>
        </div>
      </div>
    </>
  );
}

interface DataPoint {
  value: number;
  color: string;
  rating: ImpactRating;
  comparedToPreviousTimePeriod: number;
}
interface TooltipProps {
  dataPoints: Array<DataPoint>;
  impactCategory: ImpactCategory;
  intl: IntlShape;
  isSelectedLabel?: boolean;
  isPercentage: boolean;
  period: string;
  locale: string | null;
  timePeriodLabel?: string;
  previousTimePeriodLabel: string | null;
  timePeriodTitle?: string;
}

export function Tooltip(props: TooltipProps) {
  const { dataPoints } = props;

  const SINCE_PREVIOUS_TEXT = useMemo(() => {
    if (!props.previousTimePeriodLabel) return "";
    return props.intl.formatMessage(
      {
        id: "components/pages:SalesDashBoard/AreaChart/tooltip/sincePrevious",
        defaultMessage: "since {period}",
      },
      { period: props.previousTimePeriodLabel }
    );
  }, [props.intl, props.previousTimePeriodLabel]);

  const NO_DATA_TEXT = useMemo(() => {
    return props.intl.formatMessage(
      {
        id: "components/pages:SalesDashBoard/lineChartEmission/tooltip/title/noData",
        defaultMessage: "No data for {timePeriodLabel}",
      },
      {
        timePeriodLabel: props.timePeriodLabel,
      }
    );
  }, [props.intl, props.timePeriodLabel]);

  const RATINGS_TEXT = useCallback(
    (rating: ImpactRating) => {
      if (rating === "VLOW") {
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/AreaChart/tooltip/veryLow",
          defaultMessage: "Very Low",
        });
      }
      if (rating === "LOW") {
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/AreaChart/tooltip/low",
          defaultMessage: "Low",
        });
      }
      if (rating === "MEDIUM") {
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/AreaChart/tooltip/medium",
          defaultMessage: "Medium",
        });
      }
      if (rating === "HIGH") {
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/AreaChart/tooltip/high",
          defaultMessage: "High",
        });
      }
      if (rating === "VHIGH") {
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/AreaChart/tooltip/veryHigh",
          defaultMessage: "Very High",
        });
      }
    },
    [props.intl]
  );

  if (!dataPoints.length) return null;

  if (dataPoints.every((d) => d.value == null)) {
    return <div className="SalesChart_Tooltip_no_data">{NO_DATA_TEXT}</div>;
  }
  if (!props.isSelectedLabel) {
    const datapoint = dataPoints[0];

    return (
      <div
        style={{ width: "auto" }}
        className="ImpactByRating_Tooltip_RatingDistributionChart"
      >
        <div className="SalesChart_Tooltip_title">{props.timePeriodTitle}</div>

        <div
          style={{ gap: 16 }}
          className="SalesChart_Tooltip_Body d-flex justify-content-between"
        >
          <div
            className="SalesChart_Tooltip_Info"
            style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
          >
            <div className="SalesChart_Tooltip_color_Label_value">
              {RATINGS_TEXT(datapoint.rating)}
            </div>
            <div className="SalesChart_Tooltip_description mb-1">
              {impactLongCategoryLabelTitle(props.impactCategory, props.intl)}
            </div>

            <div
              style={{ flexDirection: "column", flexGrow: 1 }}
              className="d-flex"
            >
              <div
                className="SalesChart_Tooltip_color_Label_value"
                style={{ lineHeight: "24px" }}
              >
                {datapoint.value?.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}
                %
              </div>
              <div
                style={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "end",
                }}
                className="SalesChart_Tooltip_description"
              >
                {props.isPercentage
                  ? props.intl.formatMessage({
                      id: "components/pages:SalesDashBoard/AreaChart/tooltip/ofTotalSalesText",
                      defaultMessage: "of total sales",
                    })
                  : props.intl.formatMessage({
                      id: "components/pages:SalesDashBoard/AreaChart/tooltip/salesText",
                      defaultMessage: "sales",
                    })}
              </div>
            </div>
          </div>

          <div
            style={{ gap: 4 }}
            className="SalesChart_Tooltip_IconColumn d-flex flex-column align-items-start"
          >
            <Label
              colourSetting="colour"
              type="letterRating"
              impactCategory={props.impactCategory}
              impactRating={datapoint.rating}
              width={46}
              locale={null}
            />
            <div style={{ height: "20px", marginTop: 4 }}>
              <PercentageLozenge
                amount={datapoint.comparedToPreviousTimePeriod ?? 0 / 100}
                intl={props.intl}
                sentiment="neutral"
                classNameContainer="RatingChart_PercentageLozenge"
              />
            </div>
            <div className="SalesChart_Tooltip_description">
              {SINCE_PREVIOUS_TEXT}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ImpactByRating_Tooltip_RatingDistributionChart">
      <div>
        {props.isSelectedLabel ? (
          <div>
            <div style={{ gap: 8, alignItems: "center" }} className="d-flex">
              <div className="ImpactByRating_Tooltip_date_title">
                {props.intl.formatMessage({
                  id: "components/pages:SalesDashBoard/AreaChart/tooltip/total",
                  defaultMessage: "Total",
                })}
              </div>
              <div
                style={{ alignItems: "center" }}
                className="SalesChart_Tooltip_title"
              >
                <span className="SalesChart_Tooltip_description">
                  {" "}
                  {props.isPercentage ? (
                    <div className="SalesChart_Tooltip_date_title">100%</div>
                  ) : (
                    props.intl.formatMessage(
                      {
                        id: "components/pages:SalesDashBoard/AreaChart/tooltip/sales",
                        defaultMessage: "{value} sales",
                      },
                      {
                        value: (
                          <span className="SalesChart_Tooltip_date_title">
                            {dataPoints.reduce(
                              (partialSum, a) => partialSum + a.value,
                              0
                            )}
                          </span>
                        ),
                      }
                    )
                  )}
                </span>
              </div>
            </div>
            <div className="SalesChart_Tooltip_divider" />
          </div>
        ) : null}
        {dataPoints.map((datapoint, id) => {
          if (datapoint.value == null) return "";
          return (
            <div key={id}>
              <div className="d-flex ImpactByRating_Tooltip_color_container">
                <Label
                  colourSetting="colour"
                  type="letterRating"
                  impactCategory={props.impactCategory}
                  impactRating={datapoint.rating}
                  width={46}
                  locale={null}
                />
                <div
                  style={{ gap: 4 }}
                  className="SalesChart_Tooltip_column_info d-flex"
                >
                  <div className="SalesChart_Tooltip_color_Label_value">
                    {RATINGS_TEXT(datapoint.rating)}
                  </div>
                  <div
                    style={{ gap: 4, alignItems: "center" }}
                    className="SalesChart_Tooltip_description d-flex"
                  >
                    {props.isPercentage
                      ? props.intl.formatMessage(
                          {
                            id: "components/pages:SalesDashBoard/AreaChart/tooltip/ofTotalSales",
                            defaultMessage: "{value} of total sales",
                          },
                          {
                            value: (
                              <span className="SalesChart_Tooltip_color_Label_value">
                                {datapoint.value?.toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })}
                                %
                              </span>
                            ),
                          }
                        )
                      : props.intl.formatMessage(
                          {
                            id: "components/pages:SalesDashBoard/AreaChart/tooltip/sales",
                            defaultMessage: "{value} sales",
                          },
                          {
                            value: (
                              <span className="SalesChart_Tooltip_color_Label_value">
                                {datapoint.value}
                              </span>
                            ),
                          }
                        )}
                  </div>
                </div>
                <div
                  style={{ flexGrow: 1, width: "fit-content", gap: 4 }}
                  className="SalesChart_Tooltip_column_info d-flex"
                >
                  <div
                    style={{
                      height: "20px",
                      display: "flex",
                    }}
                  >
                    <PercentageLozenge
                      amount={datapoint.comparedToPreviousTimePeriod ?? 0 / 100}
                      intl={props.intl}
                      sentiment="neutral"
                      classNameContainer="RatingChart_PercentageLozenge"
                    />
                  </div>
                  <div
                    style={{ lineHeight: "20px" }}
                    className="SalesChart_Tooltip_description"
                  >
                    {SINCE_PREVIOUS_TEXT}
                  </div>
                </div>
              </div>
              {id !== dataPoints.length - 1 && (
                <div className="SalesChart_Tooltip_divider" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
